import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  PersonnelCostsResponse,
  deletePersonnelCost,
  getPersonnelCosts,
} from '@/services/PersonnelCostService';
import { useTranslation } from 'react-i18next';
import { Currency } from '@/enums/Currency';
import moment from 'moment';
import { PersonnelCategory } from '@/enums/PersonnelCategory';
import { PersonnelType } from '@/enums/PersonnelType';
const userTimeZone = moment.tz.guess();

function PersonnelCosts() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('PersonnelCosts.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('PersonnelCosts.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeletePersonnelCost.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('PersonnelCosts.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => <ActionButtonsCell value={value} />,
      },
      {
        Header: t('PersonnelCosts.functionColumn'),
        accessor: 'function',
        Filter: SelectColumnFilter,
      },
      {
        Header: t('PersonnelCosts.countryColumn'),
        accessor: 'countryName',
      },
      {
        Header: t('PersonnelCosts.categoryColumn'),
        accessor: 'category',
        Cell: ({ cell: { value } }: any) => {
          const frontendState = PersonnelCategory[value];
          //@ts-ignore
          const bgColor = 'bg-blue-400';

          return (
            <div
              className={`w-50 rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
            >
              {frontendState !== null
                ? //@ts-ignore

                  t(`PersonnelCategory.${frontendState}`)
                : ''}
            </div>
          );
        },
      },
      {
        Header: t('PersonnelCosts.typeColumn'),
        accessor: 'type',
        Cell: ({ cell: { value } }: any) => {
          const frontendState = PersonnelType[value];
          //@ts-ignore
          const bgColor = 'bg-purple-400';

          return (
            <div
              className={`w-50 rounded-md text-center ${bgColor} px-2.5  py-2.5 text-sm text-white`}
            >
              {frontendState !== null
                ? //@ts-ignore

                  t(`PersonnelType.${frontendState}`)
                : ''}
            </div>
          );
        },
      },
      {
        Header: t('PersonnelCosts.hourlyRateColumn'),
        accessor: 'hourlyRate',
      },
      {
        Header: t('PersonnelCosts.currencyColumn'),
        accessor: 'currency',
        Cell: ({ cell: { value } }) => {
          const currency = Currency[value];
          return <span>{currency}</span>;
        },
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: personnelCostsData,
  } = useQuery<PersonnelCostsResponse | undefined>(
    ['personnelCosts', pageIndex, pageSize, search],
    () =>
      getPersonnelCosts({
        fromvalue: pageIndex,
        takevalue: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeletePersonnelCost = useMutation(
    async (id: string) => {
      await deletePersonnelCost(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'personnelCosts',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((personnelCostsData?.count ?? 0) / pageSize);
  }, [pageSize, personnelCostsData]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('PersonnelCosts.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={personnelCostsData?.count}
            data={personnelCostsData?.personnelCosts ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default PersonnelCosts;
